
/**
 * Module dependencies.
 */

import { ContactLink, Link } from 'src/components/core/contacts/links';
import { ContactProps } from 'src/types/contacts';
import { color, media, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { normalizeNewLine } from 'src/core/utils/strings';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import CardTitle from 'src/components/core/typography/card-title';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Loading from 'src/components/core/loading';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement } from 'react';
import Type from 'src/components/core/typography';
import styled, { css } from 'styled-components';
import useContacts from 'src/api/app/contacts/use-contacts';

/**
 * `Props` type.
 */

type Props = {
  contacts: ContactProps[],
  description: string,
  hasPageTitle?: boolean,
  isKiosk?: boolean,
  onGotoContactFormSection: () => void,
  subtitle?: string | null,
  title: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ isLoading: boolean }>`
  background-color: ${color('beige200')};
  overflow: hidden;
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(5)}) 0 ${units(8)};
  position: relative;

  ${media.min('ms')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(10)}) 0 ${units(17)};
  `}

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'title'
    'desc'
    'contacts';
  grid-template-columns: 100%;
  grid-template-rows: repeat(3, max-content);

  ${media.min('ms')`
    grid-template-areas:
      '. title  title  .          .'
      '. desc   .      contacts   .';
    grid-template-columns: 1fr 2fr 1fr 5fr 1fr;
    grid-template-rows: repeat(2, max-content);
  `}

  ${media.min('md')`
    grid-template-areas:
      '. title  title  . .          .'
      '. desc   .      . contacts   .';
    grid-template-columns: 1fr 3fr 2fr 1fr 4fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  grid-area: title;
  margin-bottom: ${units(6)};
`;

/**
 * `DescriptionAnimation` styled component.
 */

const DescriptionAnimation = styled(FadeInUpAnimation)`
  grid-area: desc;
`;

/**
 * `DescriptionWrapper` styled component.
 */

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${units(3)};
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.H6).attrs({ as: 'p', small: true })`
  color: ${color('grey400')};
  margin-bottom: ${units(3)};
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  width: max-content;
`;

/**
 * `ContactsWrapper` styled component.
 */

const ContactsWrapper = styled.div`
  color: ${color('grey400')};
  grid-area: contacts;

  ${media.min('ms')`
    display: flex;
  `}
`;

/**
 * `Contact` styled component.
 */

const Contact = styled.div`
  ${media.max('md')`
    margin-bottom: ${units(5)};
  `}

  ${media.min('ms')`
    flex: 0 0 50%;
    padding-right: ${units(2)};
  `}
`;

/**
 * `ContactName` styled component.
 */

const ContactName = styled(CardTitle).attrs({ size: 'small' })`
  margin-bottom: ${units(2)};
`;

/**
 * `ContactMapLink` styled component.
 */

const ContactMapLink = styled.a`
  border-bottom: 1px solid ${color('brown200')};
  color: ${color('brown200')};
  display: table;
  font-size: ${units(2)};
  font-weight: 400;
  line-height: ${units(3)};
  margin-top: ${units(2)};
  text-decoration: none;
  text-transform: uppercase;
  transition: ${theme('animations.defaultTransition')};
  transition-property: border-bottom-color, color;

  &:focus,
  &:hover {
    border-bottom-color: ${color.transparentize('brown200', 0.3)};
    color: ${color.transparentize('brown200', 0.3)};
  }
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Type.H5).attrs({
  as: 'span'
})`
  display: block;
  grid-area: subTitle;
  padding-top: ${units(1)};

  ${media.min('ms')`
    padding-top: ${units(2)};
  `}
`;

/**
 * `ContactsSection` component.
 */

const ContactsSection = (props: Props): ReactElement => {
  const { translate } = useTranslate();
  const {
    description,
    hasPageTitle,
    isKiosk,
    onGotoContactFormSection,
    subtitle,
    title
  } = props;

  const {
    data: contacts,
    isLoading: isLoadingContacts,
    isSuccess: isSuccessContacts
  } = useContacts({ initialData: props?.contacts });

  const isLoading = isLoadingContacts;
  const isSuccess = isSuccessContacts;

  return (
    <Section isLoading={isLoading}>
      <Loading active={isLoading} />

      {isSuccess && (
        <Container>
          <Grid>
            <Title
              {...(hasPageTitle && {
                as: 'h1'
              })}
            >
              <FadeInUpAnimation>
                {`${title} `}

                {subtitle && (
                  <SubTitle>
                    {subtitle}
                  </SubTitle>
                )}
              </FadeInUpAnimation>
            </Title>

            <DescriptionAnimation>
              <DescriptionWrapper>
                <RawHtml element={Description}>
                  {normalizeNewLine(description)}
                </RawHtml>

                <ButtonWrapper>
                  <Button
                    onClick={onGotoContactFormSection}
                    variant={'outlined'}
                  >
                    {translate('common:actions.leaveMessage')}
                  </Button>
                </ButtonWrapper>
              </DescriptionWrapper>
            </DescriptionAnimation>

            <ContactsWrapper>
              {contacts && contacts.map(({
                description,
                email,
                fax,
                mapUrl,
                name,
                phone
              }, index: number) => (
                <FadeInUpAnimation key={index}>
                  <Contact>
                    <ContactName>
                      {name}
                    </ContactName>

                    <RawHtml element={Type.Paragraph}>
                      {normalizeNewLine(description)}
                    </RawHtml>

                    {phone && (isKiosk ? (
                      <Type.Paragraph>
                        {phone}

                        <br />
                      </Type.Paragraph>
                    ) : (
                      <div>
                        <Link i18nkey={translate('common:sections.contacts.mobile', { phone })} />
                      </div>
                    ))}

                    {fax && (isKiosk ? (
                      <Type.Paragraph>
                        {fax}

                        <br />
                      </Type.Paragraph>
                    ) : (
                      <div>
                        <Link i18nkey={translate('common:sections.contacts.fax', { fax })} />
                      </div>
                    ))}

                    {email && (isKiosk ? (
                      <Type.Paragraph>
                        {email}

                        <br />
                      </Type.Paragraph>
                    ) : (
                      <ContactLink
                        href={`mailto:${email}`}
                        rel={'noopener'}
                        target={'_blank'}
                      >
                        {email}
                      </ContactLink>
                    ))}

                    {!isKiosk && (
                      <ContactMapLink
                        href={mapUrl}
                        rel={'noopener'}
                        target={'_blank'}
                      >
                        {translate('footer:viewMap')}
                      </ContactMapLink>
                    )}
                  </Contact>
                </FadeInUpAnimation>
              ))}
            </ContactsWrapper>
          </Grid>
        </Container>
      )}
    </Section>
  );
};

/**
 * Export `ContactsSection` component.
 */

export default ContactsSection;
